/**
 * Hooks de gestão de custom arts — TH-53368 / M5.E6.S2.1.
 *
 * Complementa `useCreateCustomArt` (TH-53369). Mantemos cada operação
 * num hook próprio pra que invalidações fiquem co-localizadas com o
 * contrato HTTP — a página `/custom-arts` só compõe.
 *
 * Contrato do backend (TH-53366 + TH-55161):
 *   - GET    /custom-arts?store_id&area_id&active&scope&page&per_page
 *   - PATCH  /custom-arts/:id  body: { name?, active?, valid_from?,
 *            valid_until?, store_id?, area_id? }
 *   - DELETE /custom-arts/:id  → 204
 *
 * `scope` (TH-55161): filtra por origem — global (Priceshow), tenant
 * (Minha) ou supplier (Patrocinada). Sem scope = merge dos 3 (aba Todas).
 *
 * Filtro por `validade` é cosmético/client-side (backend não expõe
 * parâmetro). Página aplica sobre o batch atual da paginação — não
 * tenta paginar "expiradas" virtualmente porque a contagem viria
 * inconsistente com o `meta.total`.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { CustomArtRead } from "@/hooks/useCreateCustomArt";

export type { CustomArtRead };

export interface CustomArtListMeta {
  page: number;
  per_page: number;
  total: number;
}

export interface CustomArtListResponse {
  data: CustomArtRead[];
  meta: CustomArtListMeta;
}

/** Mapeamento de aba de UI → param `scope` que o backend entende (TH-55161). */
export type CustomArtTab = "Todas" | "Minhas" | "Priceshow" | "Patrocinadas";
export type CustomArtScope = "global" | "tenant" | "supplier";

const TAB_TO_SCOPE: Record<CustomArtTab, CustomArtScope | undefined> = {
  Todas: undefined,
  Minhas: "tenant",
  Priceshow: "global",
  Patrocinadas: "supplier",
};

export interface CustomArtListFilters {
  store_id?: string;
  area_id?: string;
  active?: boolean;
  /** Aba selecionada na UI — mapeada para `scope` no request. */
  tab?: CustomArtTab;
  /** Busca por nome (server-side, debounced na página). */
  search?: string;
  page?: number;
  per_page?: number;
}

function buildQuery(filters: CustomArtListFilters): string {
  const sp = new URLSearchParams();
  if (filters.store_id) sp.set("store_id", filters.store_id);
  if (filters.area_id) sp.set("area_id", filters.area_id);
  if (filters.active !== undefined) sp.set("active", String(filters.active));
  if (filters.tab && filters.tab !== "Todas") {
    const scope = TAB_TO_SCOPE[filters.tab];
    if (scope) sp.set("scope", scope);
  }
  if (filters.search?.trim()) sp.set("search", filters.search.trim());
  sp.set("page", String(filters.page ?? 1));
  sp.set("per_page", String(filters.per_page ?? 20));
  return sp.toString();
}

async function fetchCustomArts(
  filters: CustomArtListFilters,
): Promise<CustomArtListResponse> {
  const res = await fetch(`/api/custom-arts?${buildQuery(filters)}`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as CustomArtListResponse;
}

export function useCustomArtsList(
  filters: CustomArtListFilters,
): UseQueryResult<CustomArtListResponse, Error> {
  // Object como key é serializado pelo TanStack Query — só refetcha
  // quando filtros mudam de verdade (não em re-render).
  // Inclui `tab` e `search` na key pra que mudanças de aba ou busca
  // provoquem novo fetch (TH-55166).
  return useQuery({
    queryKey: ["custom-arts", filters],
    queryFn: () => fetchCustomArts(filters),
    staleTime: 30_000,
  });
}

export interface CustomArtUpdateInput {
  id: string;
  name?: string;
  active?: boolean;
  valid_from?: string | null;
  valid_until?: string | null;
  store_id?: string | null;
  area_id?: string | null;
}

async function patchCustomArt(
  input: CustomArtUpdateInput,
): Promise<CustomArtRead> {
  const { id, ...payload } = input;
  const res = await fetch(`/api/custom-arts/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as CustomArtRead;
}

export function useCustomArtUpdate(): UseMutationResult<
  CustomArtRead,
  Error,
  CustomArtUpdateInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchCustomArt,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["custom-arts"] }),
  });
}

async function deleteCustomArt(id: string): Promise<void> {
  const res = await fetch(`/api/custom-arts/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

export function useCustomArtDelete(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCustomArt,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["custom-arts"] }),
  });
}
